import React, { useRef } from "react";

/**
 * Five sea-themed pause animations for the 30-second grounding moment.
 * One is randomly selected each visit. All accept `progress` (0 -> 1).
 *
 * - jellyfish     : a translucent jellyfish drifting upward
 * - breath        : a slow-pulsing pearl bubble (CSS-only)
 * - tide          : waves rising up the shore, a shell uncovered at the end
 * - constellation : moonlit pinpricks of light on the water's surface
 * - shell         : a scallop slowly opening to reveal a pearl
 */

export const PAUSE_VARIANTS = [
  "jellyfish",
  "breath",
  "tide",
  "constellation",
  "shell",
];

export function PauseAnimation({ variant, progress }) {
  switch (variant) {
    case "jellyfish":
      return <JellyfishDrift progress={progress} />;
    case "breath":
      return <BreathOrb />;
    case "tide":
      return <TideRise progress={progress} />;
    case "constellation":
      return <Moonlight progress={progress} />;
    case "shell":
      return <ShellOpen progress={progress} />;
    default:
      return <BreathOrb />;
  }
}

/* ------------- JELLYFISH ------------- */
function JellyfishDrift({ progress }) {
  // drifts upward and gently scales as it goes
  const y = 40 - progress * 70;       // from 40 (lower) to -30 (higher)
  const swayPhase = progress * Math.PI * 4;
  const sway = Math.sin(swayPhase) * 4;
  const bellScale = 1 + Math.sin(progress * Math.PI * 6) * 0.06;
  return (
    <div style={{ width: 150, height: 180, margin: "0 auto" }} data-testid="pause-jellyfish">
      <svg viewBox="0 0 150 180" style={{ width: "100%", height: "100%" }}>
        <defs>
          <radialGradient id="jellyBell" cx="50%" cy="40%" r="60%">
            <stop offset="0%" stopColor="rgba(232, 238, 245, 0.95)" />
            <stop offset="55%" stopColor="rgba(143, 196, 224, 0.55)" />
            <stop offset="100%" stopColor="rgba(58, 124, 165, 0.08)" />
          </radialGradient>
        </defs>
        <g style={{ transform: `translate(${sway}px, ${y}px)`, transformOrigin: "75px 90px", transition: "transform 0.1s linear" }}>
          {/* tentacles — curly threads */}
          {[-22, -14, -6, 2, 10, 18].map((dx, i) => {
            const tx = 75 + dx;
            const wiggle = Math.sin(swayPhase + i) * 3;
            return (
              <path
                key={i}
                d={`M ${tx} 100 Q ${tx + wiggle} 120 ${tx - wiggle * 0.6} 140 Q ${tx + wiggle * 0.5} 158 ${tx} 174`}
                fill="none"
                stroke="rgba(143, 196, 224, 0.55)"
                strokeWidth={i === 2 || i === 3 ? 1.1 : 0.7}
                strokeLinecap="round"
              />
            );
          })}
          {/* longer trailing tentacles */}
          {[-10, 6].map((dx, i) => (
            <path
              key={`t${i}`}
              d={`M ${75 + dx} 102 Q ${75 + dx - 6} 130 ${75 + dx + 4} 158 Q ${75 + dx - 2} 178 ${75 + dx} 198`}
              fill="none"
              stroke="rgba(232, 238, 245, 0.4)"
              strokeWidth="0.7"
              strokeLinecap="round"
            />
          ))}
          {/* bell */}
          <g style={{ transform: `scale(${bellScale})`, transformOrigin: "75px 80px" }}>
            <path d="M40 92 Q40 50 75 50 Q110 50 110 92 Q108 100 100 100 L50 100 Q42 100 40 92 Z" fill="url(#jellyBell)" />
            {/* faint bell ribs */}
            <path d="M52 96 Q52 64 75 56" stroke="rgba(232,238,245,0.35)" strokeWidth="0.7" fill="none" />
            <path d="M98 96 Q98 64 75 56" stroke="rgba(232,238,245,0.35)" strokeWidth="0.7" fill="none" />
            <path d="M75 56 L75 100" stroke="rgba(232,238,245,0.25)" strokeWidth="0.5" fill="none" />
            {/* inner glow dot */}
            <circle cx="75" cy="78" r="6" fill="rgba(232, 238, 245, 0.55)" />
            <circle cx="75" cy="78" r="2.5" fill="rgba(232, 238, 245, 0.95)" />
          </g>
        </g>
        {/* tiny rising bubbles trailing */}
        {progress > 0.15 && (
          <>
            <circle cx={68 + sway} cy={150 - progress * 90} r="1.4" fill="rgba(232,238,245,0.55)" />
            <circle cx={84 + sway * 0.6} cy={170 - progress * 110} r="1" fill="rgba(232,238,245,0.4)" />
          </>
        )}
      </svg>
    </div>
  );
}

/* ------------- BREATH ORB / PEARL BUBBLE ------------- */
function BreathOrb() {
  return (
    <div style={{ width: 180, height: 180, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "center" }} data-testid="pause-breath">
      <div className="breath-orb" />
    </div>
  );
}

/* ------------- TIDE RISING ------------- */
function TideRise({ progress }) {
  // water level rises from bottom; multi-layered waves shift phase
  const waterTop = 200 - progress * 130; // from y=200 down to y=70
  const phase = progress * Math.PI * 4;
  const wave = (amp, off, freq) => {
    const pts = [];
    for (let x = 0; x <= 200; x += 8) {
      const y = waterTop + Math.sin((x / 200) * Math.PI * freq + phase + off) * amp;
      pts.push(`${x},${y.toFixed(1)}`);
    }
    return `M ${pts[0]} L ${pts.slice(1).join(" L ")} L 200,210 L 0,210 Z`;
  };
  return (
    <div style={{ width: 200, height: 210, margin: "0 auto" }} data-testid="pause-tide">
      <svg viewBox="0 0 200 210" style={{ width: "100%", height: "100%" }}>
        {/* sand floor — gradient */}
        <defs>
          <linearGradient id="sand" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#3a3a45" />
            <stop offset="100%" stopColor="#1a1a25" />
          </linearGradient>
        </defs>
        <rect x="0" y="195" width="200" height="15" fill="url(#sand)" opacity="0.7" />
        {/* a shell partially buried in sand, revealed as tide rises past it */}
        <g opacity={Math.min(progress * 1.4, 1)}>
          <path d="M86 200 Q100 188 114 200" fill="none" stroke="rgba(232,238,245,0.65)" strokeWidth="1.3" />
          <path d="M86 200 Q100 194 114 200" fill="rgba(232,238,245,0.25)" />
          {Array.from({ length: 5 }).map((_, i) => {
            const t = (i + 1) / 6;
            const ang = Math.PI + t * Math.PI;
            const x1 = 100 + Math.cos(ang) * 3;
            const y1 = 200 + Math.sin(ang) * 3;
            const x2 = 100 + Math.cos(ang) * 12;
            const y2 = 200 + Math.sin(ang) * 12;
            return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="rgba(232,238,245,0.55)" strokeWidth="0.6" />;
          })}
        </g>
        {/* deepest wave layer */}
        <path d={wave(2.5, 0, 3)} fill="rgba(11, 20, 42, 0.85)" />
        {/* mid wave */}
        <path d={wave(3.5, 1.2, 2.5)} fill="rgba(30, 58, 95, 0.7)" />
        {/* surface wave with foam */}
        <path d={wave(4.5, 2.4, 2)} fill="rgba(58, 124, 165, 0.55)" />
        {/* foam highlights */}
        {Array.from({ length: 5 }).map((_, i) => {
          const x = 20 + i * 40;
          const y = waterTop + Math.sin((x / 200) * Math.PI * 2 + phase + 2.4) * 4.5;
          return <circle key={i} cx={x} cy={y - 1} r="1.2" fill="rgba(232,238,245,0.75)" />;
        })}
        {/* a few floating bubbles above water */}
        {progress > 0.4 && (
          <>
            <circle cx="60" cy={waterTop - 14} r="2" fill="rgba(232,238,245,0.6)" />
            <circle cx="140" cy={waterTop - 22} r="1.5" fill="rgba(232,238,245,0.5)" />
            <circle cx="100" cy={waterTop - 8} r="1.2" fill="rgba(232,238,245,0.55)" />
          </>
        )}
      </svg>
    </div>
  );
}

/* ------------- MOONLIGHT / CONSTELLATION ------------- */
function Moonlight({ progress }) {
  // Stable star positions generated once per mount
  const starsRef = useRef(null);
  if (!starsRef.current) {
    const pts = [];
    for (let i = 0; i < 9; i++) {
      pts.push({
        x: 25 + Math.random() * 130,
        y: 25 + Math.random() * 90,
        r: 1.4 + Math.random() * 1.6,
      });
    }
    starsRef.current = pts;
  }
  const stars = starsRef.current;
  const visibleCount = Math.floor(progress * stars.length);
  // water ripple progress
  const ripple = progress;
  return (
    <div style={{ width: 180, height: 180, margin: "0 auto" }} data-testid="pause-constellation">
      <svg viewBox="0 0 180 180" style={{ width: "100%", height: "100%" }}>
        {/* moon */}
        <circle cx="138" cy="32" r="11" fill="rgba(232,238,245,0.95)" opacity="0.92" />
        <circle cx="138" cy="32" r="16" fill="rgba(232,238,245,0.18)" />
        {/* connecting lines between stars */}
        {stars.slice(0, Math.max(visibleCount - 1, 0)).map((p, i) => {
          const next = stars[i + 1];
          if (!next) return null;
          return (
            <line
              key={`l${i}`}
              x1={p.x} y1={p.y} x2={next.x} y2={next.y}
              stroke="rgba(143, 196, 224, 0.5)"
              strokeWidth="0.6"
              strokeDasharray="2 3"
            />
          );
        })}
        {stars.map((p, i) => (
          <g key={`s${i}`} style={{ opacity: i < visibleCount ? 1 : 0, transition: "opacity 0.6s ease" }}>
            <circle cx={p.x} cy={p.y} r={p.r * 2.2} fill="#E8EEF5" opacity="0.18" />
            <circle className="star" cx={p.x} cy={p.y} r={p.r} fill="#E8EEF5" style={{ animationDelay: `${i * 0.3}s` }} />
          </g>
        ))}
        {/* water surface */}
        <path d="M0 130 Q45 124 90 130 T180 130 L180 180 L0 180 Z" fill="rgba(11, 20, 42, 0.85)" />
        <path d="M0 130 Q45 124 90 130 T180 130" stroke="rgba(143, 196, 224, 0.5)" strokeWidth="0.7" fill="none" />
        {/* moon reflection — vertical column of broken light */}
        {[0, 1, 2, 3, 4].map((i) => {
          const y = 138 + i * 8;
          const w = 18 - i * 1.5 + Math.sin(ripple * Math.PI * 3 + i) * 2;
          return <ellipse key={i} cx="138" cy={y} rx={w} ry="1.3" fill="rgba(232,238,245,0.55)" opacity={0.65 - i * 0.1} />;
        })}
      </svg>
    </div>
  );
}

/* ------------- SHELL OPENING ------------- */
function ShellOpen({ progress }) {
  // top half of shell rotates open from 0 to ~32deg, revealing a pearl that grows
  const lidAngle = progress * 32; // degrees
  const pearlScale = Math.max((progress - 0.35) / 0.65, 0);
  const pearlGlow = pearlScale > 0.95 ? 1 : pearlScale;
  return (
    <div style={{ width: 160, height: 180, margin: "0 auto" }} data-testid="pause-shell">
      <svg viewBox="0 0 160 180" style={{ width: "100%", height: "100%" }}>
        <defs>
          <radialGradient id="pearl" cx="40%" cy="35%" r="65%">
            <stop offset="0%" stopColor="#FFFFFF" />
            <stop offset="55%" stopColor="#E1E7E5" />
            <stop offset="100%" stopColor="#8FC4E0" />
          </radialGradient>
          <linearGradient id="shellTop" x1="0" y1="1" x2="0" y2="0">
            <stop offset="0%" stopColor="#C8D4E0" />
            <stop offset="100%" stopColor="#E8EEF5" />
          </linearGradient>
          <linearGradient id="shellBot" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#C8D4E0" />
            <stop offset="100%" stopColor="#8FA3B8" />
          </linearGradient>
        </defs>
        {/* faint sand pool */}
        <ellipse cx="80" cy="160" rx="58" ry="6" fill="rgba(143, 196, 224, 0.12)" />
        <ellipse cx="80" cy="158" rx="46" ry="3" fill="rgba(143, 196, 224, 0.22)" />

        {/* pearl */}
        <g style={{ transform: `scale(${pearlScale})`, transformOrigin: "80px 110px", transition: "transform 0.3s ease" }}>
          {/* halo */}
          <circle cx="80" cy="108" r="22" fill="rgba(232, 238, 245, 0.18)" opacity={pearlGlow} />
          <circle cx="80" cy="108" r="14" fill="url(#pearl)" />
          <circle cx="74" cy="102" r="3" fill="rgba(255,255,255,0.85)" />
        </g>

        {/* bottom shell half (stays still) */}
        <g>
          <path d="M22 110 Q80 168 138 110 Q132 122 122 130 L38 130 Q28 122 22 110 Z" fill="url(#shellBot)" />
          {Array.from({ length: 9 }).map((_, i) => {
            const t = i / 8;
            const ang = Math.PI * (1 + t);
            const x1 = 80 + Math.cos(ang) * 6;
            const y1 = 110 + Math.sin(ang) * 6;
            const x2 = 80 + Math.cos(ang) * 48;
            const y2 = 110 + Math.sin(ang) * 48;
            return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="rgba(11, 20, 42, 0.35)" strokeWidth="0.7" />;
          })}
          <circle cx="80" cy="110" r="2" fill="rgba(11, 20, 42, 0.6)" />
        </g>

        {/* top shell half — rotates open from the hinge (80,110) */}
        <g style={{ transform: `rotate(${-lidAngle}deg)`, transformOrigin: "80px 110px", transition: "transform 0.1s linear" }}>
          <path d="M22 110 Q80 52 138 110 Q132 98 122 90 L38 90 Q28 98 22 110 Z" fill="url(#shellTop)" />
          {Array.from({ length: 9 }).map((_, i) => {
            const t = i / 8;
            const ang = -Math.PI * t;
            const x1 = 80 + Math.cos(ang) * 6;
            const y1 = 110 + Math.sin(ang) * 6;
            const x2 = 80 + Math.cos(ang) * 48;
            const y2 = 110 + Math.sin(ang) * 48;
            return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="rgba(11, 20, 42, 0.35)" strokeWidth="0.7" />;
          })}
        </g>

        {/* tiny bubbles released as it opens */}
        {progress > 0.5 && (
          <>
            <circle cx={56 + progress * 4} cy={92 - progress * 30} r="1.2" fill="rgba(232,238,245,0.55)" opacity={1 - progress * 0.6} />
            <circle cx={104 - progress * 3} cy={86 - progress * 36} r="0.9" fill="rgba(232,238,245,0.5)" opacity={1 - progress * 0.6} />
            <circle cx={80} cy={78 - progress * 24} r="0.7" fill="rgba(232,238,245,0.45)" opacity={1 - progress * 0.6} />
          </>
        )}
      </svg>
    </div>
  );
}
